// src/tabs/ToolsTab.js

import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  TextField,
  Grid,
  Alert
} from "@mui/material";

function ToolsTab({ tools, fetchTools }) {
  // Tool seleccionada
  const [selectedTool, setSelectedTool] = useState(null);

  // Campos del formulario
  const [toolName, setToolName] = useState("");
  const [toolDescription, setToolDescription] = useState("");
  const [toolConfigStr, setToolConfigStr] = useState("{}");

  // Control de errores y resultado
  const [loading, setLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [result, setResult] = useState(null);

  // Seleccionar una tool de la lista
  const handleSelectTool = (tool) => {
    setSelectedTool(tool);
    setResult(null);
    setToolName(tool.tool_name || "");         // <-- Fijarse que aquí usamos tool.tool_name
    setToolDescription(tool.description || "");
    setToolConfigStr(tool.config ? JSON.stringify(tool.config, null, 2) : "{}");
    setConfigError(null);
  };

  // Crear nueva tool (limpia formulario)
  const handleCreateNewTool = () => {
    setSelectedTool(null);
    setResult(null);
    setToolName("");
    setToolDescription("");
    setToolConfigStr("{}");
    setConfigError(null);
  };

  // Guardar o crear tool (POST)
  const handleSubmitTool = async (e) => {
    e.preventDefault();
    setLoading(true);
    setConfigError(null);

    let parsedConfig;
    try {
      parsedConfig = JSON.parse(toolConfigStr);
    } catch (err) {
      setConfigError(`Error al parsear JSON: ${err.message}`);
      setLoading(false);
      return;
    }

    // Ajustamos 'tool_name' en lugar de 'name'
    const dataToSend = {
      name: toolName,          // <-- IMPORTANTE
      description: toolDescription,
      config: parsedConfig
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/tool`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        console.error("Error al crear/actualizar tool:", response.status);
      }

      const resultJson = await response.json();
      setResult(resultJson);

      // Refresca la lista global de tools
      fetchTools();
    } catch (error) {
      console.error("Error en la petición:", error);
      setResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      {/* Lista de Tools */}
      <Paper sx={{ width: 260, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Lista de Tools
        </Typography>
        <List>
          {tools.map((tool) => (
            <ListItemButton
              key={tool.id}
              selected={selectedTool && selectedTool.id === tool.id}
              onClick={() => handleSelectTool(tool)}
            >
              <ListItemText primary={tool.tool_name} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ textAlign: "center", p: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreateNewTool}
          >
            Crear Nueva Tool
          </Button>
        </Box>
      </Paper>

      {/* Formulario para la Tool */}
      <Box sx={{ flexGrow: 1 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {selectedTool
              ? `Editando: ${selectedTool.tool_name}`
              : "Creando nueva Tool"}
          </Typography>

          <Box component="form" onSubmit={handleSubmitTool} noValidate sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Tool Name"
                  variant="outlined"
                  fullWidth
                  value={toolName}
                  onChange={(e) => setToolName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  variant="outlined"
                  multiline
                  rows={2}
                  fullWidth
                  value={toolDescription}
                  onChange={(e) => setToolDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Config (JSON)"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={6}
                  value={toolConfigStr}
                  onChange={(e) => setToolConfigStr(e.target.value)}
                  required
                />
              </Grid>

              {configError && (
                <Grid item xs={12}>
                  <Alert severity="error">{configError}</Alert>
                </Grid>
              )}

              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? "Enviando..." : "Guardar Tool"}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {result && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Respuesta del Servidor:</Typography>
              <Paper sx={{ p: 2, mt: 1, backgroundColor: "#f5f5f5" }}>
                <pre>{JSON.stringify(result, null, 2)}</pre>
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default ToolsTab;
